import { APP_BASE_HREF } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationStart, Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { Store } from '@ngxs/store';
import _get from 'lodash.get';
import { combineLatest, Subscription } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import {
  AgentService,
  AppThemeService,
  AUTH_STATUS_CODES,
  ConfigResourceService,
  LocaleService,
  NavigationContext,
  NavigationUtilityService,
  PlanYearService,
  RedirectService,
  ResponsiveLayoutService,
  RuntimeState,
  SiteService,
  SiteType,
  SNPService,
  SpinnerEvent,
  SpinnerService,
  WeglotService
} from 'vnext-shared';
import { PageSpinnerComponent, VocFeedbackComponent } from 'vnext-widgets';
import { SessionIdleService } from './core/session/idle/idle.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [RouterOutlet, PageSpinnerComponent, VocFeedbackComponent]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  showLoaderMessage: boolean = false;
  loaderText: string | null = 'Loading...';
  loaderIcon: string = '';
  iconEnabled: boolean = false;
  currentLanguage: string = '';

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly titleService: Title,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationContext: NavigationContext,
    private readonly applicationTheme: AppThemeService,
    private readonly responsiveLayoutService: ResponsiveLayoutService,
    private readonly sessionIdleService: SessionIdleService,
    private readonly spinnerService: SpinnerService,
    private readonly configResourceService: ConfigResourceService,
    private readonly localeService: LocaleService,
    private readonly snpService: SNPService,
    private readonly siteService: SiteService,
    private readonly planYearService: PlanYearService,
    private readonly weglotService: WeglotService,
    private readonly store: Store,
    private readonly redirectService: RedirectService,
    private readonly navigationUtilityService: NavigationUtilityService,
    private readonly agentService: AgentService,
    @Inject(APP_BASE_HREF) private readonly baseHref: string
  ) {
    setTimeout(() => {
      document.querySelector('#initial-la-ball-spin-clockwise')?.remove();
    });
  }

  ngOnInit(): void {
    this.setDestinationPageForLoginRedirect();
    this.sessionIdleService.initializeIdle();
    this.responsiveLayoutService.isCompact$.subscribe({
      next: compact => {
        this.applicationTheme.initCreateAppTheme(compact);
      }
    });
    this.iconEnabled = this.configResourceService.getProcessedResourceValue('Spa_Site_Loader_Updated_Icon_Enabled', null, false);
    this.showLoaderMessage =
      this.iconEnabled && this.configResourceService.getProcessedResourceValue('Spa_Site_Loader_Message_Enabled', null, true);

    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe({
        next: event => {
          if (this.showLoaderMessage) {
            this.loaderText = this.spinnerService.getLoaderMessage(
              event.url,
              this.configResourceService.getProcessedResourceValue('Spa_Site_Loader_Message_Json', null, [])
            );
          }

          // Second parameter (name) can't be null, because that's causing a non show up Spiner
          this.spinnerService.show(SpinnerEvent.NAVIGATION, undefined, this.loaderText);
        }
      })
    );

    // listening for when we navigate to a new page so we can update the document title and when
    // we change locale
    this.subscriptions.add(
      combineLatest([
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          map(() => this.activatedRoute),
          map(route => {
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }),
          filter(route => route.outlet === 'primary'),
          mergeMap(route => route.data)
        ),
        this.localeService.getCurrentLocale$()
      ]).subscribe({
        next: ([event]) => {
          this.spinnerService.hide(SpinnerEvent.NAVIGATION);
          this.loaderText = null;
          const processedEventTitle = this.configResourceService.processValueProviders(event.title);
          this.titleService.setTitle(processedEventTitle || event.title);
        }
      })
    );

    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationCancel)).subscribe({
        next: () => {
          this.spinnerService.hide(SpinnerEvent.NAVIGATION);
          this.loaderText = null;
        }
      })
    );

    this.subscriptions.add(
      this.spinnerService
        .getLoaderText$()
        .pipe(filter(loaderText => loaderText !== this.loaderText))
        .subscribe({
          next: loaderText => {
            this.loaderText = loaderText;
          }
        })
    );

    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
        next: event => {
          this.navigationContext.setUrl(_get(event, 'urlAfterRedirects'));
        }
      })
    );

    this.initializeLocale();
    this.initializeSNPTypeVisibility();
    this.planYearService.initializePlanYearPermissionMediator();
    this.loaderIcon = this.iconEnabled ? 'ball-spin-clockwise' : 'ball-scale-multiple';

    const injectJQuery = this.configResourceService.getProcessedResourceValue('Spa_Site_Enable_JQuery', null, false);
    if (injectJQuery) {
      this.injectJqueryToHead();
    }

    const injectGoogleTranslate = this.configResourceService.getProcessedResourceValue('Spa_Site_Enable_GoogleTranslate', null, false);
    if (injectGoogleTranslate) {
      this.injectGoogleTranslate();
    }

    const siteType = this.configResourceService.getProcessedResourceValue('Spa_Site_SiteType', null, SiteType.CONSUMER);
    // performance: preload demographic background images
    if (!this.siteService.isPPCSite(siteType)) {
      this.preloadImages();
    }

    const isConsumerSiteIdEnabled: boolean = this.configResourceService.getProcessedResourceValue(
      'Spa_ConsumerSiteID_Enabled',
      null,
      false
    );

    const hasMultiBrandingSetup: boolean = this.hasMultiBrandingSetup();
    if (isConsumerSiteIdEnabled) {
      this.setConsumerSiteUrls(hasMultiBrandingSetup);
    } else {
      if (hasMultiBrandingSetup) {
        this.setConsumerSiteUrls(true);
      }
    }

    const agencyConsumerResourceEnabled = this.configResourceService.getProcessedResourceValue(
      'Spa_Agency_Consumer_Resource_Sharing_Enabled',
      null,
      false
    );
    const agencyContactConsumerResourceEnabled = this.configResourceService.getProcessedResourceValue(
      'Spa_AgencyContact_Consumer_Resource_Sharing_Enabled',
      null,
      false
    );
    if (agencyConsumerResourceEnabled && agencyContactConsumerResourceEnabled) {
      this.siteService.initializeContactInfoResource();
    }
  }

  private setConsumerSiteUrls(hasMultiBrandingSetup: boolean): void {
    this.store
      .select(RuntimeState.authStatus)
      .pipe(
        filter(authStatus => authStatus === AUTH_STATUS_CODES.JWT_LOADED || authStatus === AUTH_STATUS_CODES.JWT_SAVED),
        take(1)
      )
      .subscribe({
        next: consumerSiteUrlItems => {
          this.siteService.initializeConsumerSiteUrls(hasMultiBrandingSetup);
        }
      });
  }

  private hasMultiBrandingSetup(): boolean {
    const isMultiBrandingEnabled: boolean = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_MultiBranding_Enabled',
      null,
      false
    );
    const hasMultiBrandingJsonConfig: boolean = !!this.configResourceService.getProcessedResourceValue(
      'Spa_Site_MultiBranding_JSON',
      null,
      undefined
    );
    return isMultiBrandingEnabled && hasMultiBrandingJsonConfig;
  }

  private setDestinationPageForLoginRedirect(): void {
    const isRedirectToDestinationPageAfterLoginEnabled = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_IsRedirectToDestinationPageAfterLogin_Enabled',
      null,
      true
    );
    if (isRedirectToDestinationPageAfterLoginEnabled) {
      // AppComponent.ngOnInit runs AFTER the router's initial navigation has already fired (and,
      // for an unauthenticated deeplink on a broker site, already been cancelled by the auth guard
      // which redirected to the login page). That means a `router.events` subscription registered
      // here is too late to ever observe the original deeplink NavigationStart. The browser's
      // location, however, still holds the original deeplink URL at this point, so capture it
      // synchronously from window.location.
      this.captureDestinationFromCurrentLocation();

      // Fallback: also listen for the first eligible navigation. This covers any flow where the
      // destination is reached via a later in-app navigation rather than the initial page load.
      this.subscriptions.add(
        this.router.events
          .pipe(
            filter((event): event is NavigationStart => event instanceof NavigationStart),
            filter(
              event =>
                this.redirectService.isAppInitialLoad() &&
                this.redirectService.getRedirectUrl() === null &&
                this.siteService.isPPCSite() &&
                !this.navigationUtilityService.isAgentLoginPage(event.url) &&
                !this.navigationUtilityService.isResetPasswordPage(event.url) &&
                !this.navigationUtilityService.isBaseHref(event.url) &&
                !this.agentService.isAgentLoggedIn()
            ),
            take(1)
          )
          .subscribe({
            next: event => {
              this.redirectService.setRedirectUrl(event.url);
            }
          })
      );
    }
  }

  /**
   * Capture the destination page for post-login redirect directly from the current browser
   * location. Runs synchronously during ngOnInit because the router's initial navigation has
   * already completed by the time this component initializes, so the original deeplink URL can no
   * longer be observed from router events.
   */
  private captureDestinationFromCurrentLocation(): void {
    // Build an app-relative URL (path + query), stripping the base href so the value starts with
    // '/' as required by RedirectService.setRedirectUrl and router.navigateByUrl.
    const rawPathAndQuery = `${window.location.pathname}${window.location.search}`;
    const appRelativeUrl = this.toAppRelativeUrl(rawPathAndQuery);

    if (
      this.redirectService.isAppInitialLoad() &&
      this.redirectService.getRedirectUrl() === null &&
      this.siteService.isPPCSite() &&
      !this.navigationUtilityService.isAgentLoginPage(appRelativeUrl) &&
      !this.navigationUtilityService.isResetPasswordPage(appRelativeUrl) &&
      !this.navigationUtilityService.isBaseHref(appRelativeUrl) &&
      !this.agentService.isAgentLoggedIn()
    ) {
      this.redirectService.setRedirectUrl(appRelativeUrl);
    }
  }

  /**
   * Strip the application base href from an absolute path so the result is router-relative and
   * begins with a single leading slash. Falls back to the original path if it does not start with
   * the base href.
   */
  private toAppRelativeUrl(pathAndQuery: string): string {
    const normalizedBase = this.baseHref?.replace(/\/+$/, '') ?? '';
    let relative = pathAndQuery;
    if (normalizedBase && pathAndQuery.startsWith(normalizedBase)) {
      relative = pathAndQuery.slice(normalizedBase.length);
    }
    if (!relative.startsWith('/')) {
      relative = `/${relative}`;
    }
    return relative;
  }

  ngAfterViewInit(): void {
    const weglotMultiLanguageEnabled = this.configResourceService.getProcessedResourceValue(
      'Spa_Weglot_MultiLanguage_Enabled',
      null,
      false
    );
    this.weglotService.initializeWeglot(weglotMultiLanguageEnabled);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected injectJqueryToHead(): void {
    this.injectScriptToHead('https://cdnjs.cloudflare.com/ajax/libs/jquery/3.4.1/jquery.min.js');
  }

  protected injectGoogleTranslate(): void {
    this.injectGoogleTranslateConfiguration();
    this.injectScriptToHead('https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit');
  }

  private preloadImages(): void {
    const homePageBackgroundImageDesktop = this.configResourceService.getProcessedResourceValue('Spa_Demographic_Background_Url', null, '');
    const homePageBackgroundImageMobile = this.configResourceService.getProcessedResourceValue(
      'Spa_Demographic_Background_UrlMobile',
      null,
      ''
    );

    this.injectPreloadLinkToHead(homePageBackgroundImageDesktop, 'image');
    this.injectPreloadLinkToHead(homePageBackgroundImageMobile, 'image');
  }

  private initializeLocale(): void {
    const weglotMultiLanguageEnabled = this.configResourceService.getProcessedResourceValue(
      'Spa_Weglot_MultiLanguage_Enabled',
      null,
      false
    );

    // we cannot used activatedRoute in a component that is not rendered in the router-outlet chain
    // which AppComponent can't be since it is the component to define the router-outlet
    // therefore we will use native JS to get the culture code so we can set it immediately
    // as setting it later results in the locale pickers lagging behind the locale change
    const urlParams = new URLSearchParams(window.location.search);
    const queryParamsCultureCode = urlParams.get('CultureCode') || urlParams.get('lang');
    const currentLocale = queryParamsCultureCode ?? this.localeService.getCurrentLocale();
    if (!weglotMultiLanguageEnabled && currentLocale && this.localeService.isValidLocale(currentLocale)) {
      this.localeService.setLocale(currentLocale);
    } else {
      this.initializeDefaultLocale();
    }

    if (weglotMultiLanguageEnabled) {
      this.weglotService.setDefaultLocale();
      if (queryParamsCultureCode) {
        this.weglotService.setLocale(queryParamsCultureCode);
      }
    }
  }

  private initializeSNPTypeVisibility(): void {
    const defaultVisibility: boolean = this.configResourceService.getProcessedResourceValue(
      'Spa_Site_ShowSNPPlansDefault_Flag',
      null,
      false
    );
    this.snpService.initializeSNPTypeVisibility(defaultVisibility);
  }

  /**
   * Set the default locale using Spa_Site_Default_Locale, then the browser language, then the application default
   */
  private initializeDefaultLocale(): void {
    const siteDefaultLocale = this.configResourceService.getProcessedResourceValue('Spa_Site_Default_Locale', null, 'en-US');
    if (siteDefaultLocale && this.localeService.isValidLocale(siteDefaultLocale)) {
      this.localeService.setLocale(siteDefaultLocale);
    } else {
      const browserLanguage = navigator?.language;
      if (browserLanguage && this.localeService.isValidLocale(siteDefaultLocale)) {
        this.localeService.setLocale(browserLanguage);
      } else {
        this.localeService.setLocale(this.localeService.getDefaultLocale());
      }
    }
  }

  private injectScriptToHead(src: string, onload?: ((this: GlobalEventHandlers, ev: Event) => any) | null): void {
    if (src) {
      const scriptElement: HTMLScriptElement = document.createElement('script');
      scriptElement.type = 'text/javascript';
      scriptElement.src = src;
      scriptElement.async = false;
      if (onload) {
        scriptElement.onload = onload;
      }
      // find old script to replace if there
      const currentScript = document.querySelector(`script[src='${src}']`);
      if (currentScript?.parentNode) {
        currentScript.parentNode.removeChild(currentScript);
      }
      document.head.append(scriptElement);
    }
  }

  private injectGoogleTranslateConfiguration(): void {
    const scriptElement: HTMLScriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.innerHTML = `function googleTranslateElementInit() {
          new google.translate.TranslateElement({ pageLanguage: 'en', layout: google.translate.TranslateElement.InlineLayout.VERTICAL }, 'google_translate_element');
        }`;
    document.head.append(scriptElement);
  }

  private injectPreloadLinkToHead(href: string, as: string, onload?: (ev: Event) => any): void {
    if (href) {
      const linkElement: HTMLLinkElement = document.createElement('link');
      linkElement.href = href;
      linkElement.rel = 'preload';
      linkElement.as = as;
      if (onload) {
        linkElement.onload = onload;
      }
      // find old link to replace if there
      const currentLink = document.querySelector(`link[href='${href}']`);
      if (currentLink?.parentNode) {
        currentLink.parentNode.removeChild(currentLink);
      }
      document.head.append(linkElement);
    }
  }
}
